import React, { useEffect, useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  FlaskConical,
  Link2,
  MessageSquareText,
  Network,
  UploadCloud,
  UserCheck,
  UtensilsCrossed,
} from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import biomarkersScreen from '@/assets/images/landing/feature-screens/biomarkers.svg';
import brandMarkLogo from '@/assets/images/lovable-uploads/cc1c4082-b52d-4f93-9c6a-0bc389e4be79.png';
import {
  BIOMARKER_FAQS,
  BIOMARKER_META_DESCRIPTION,
  BIOMARKER_PAGE_CANONICAL_URL,
  BIOMARKER_PAGE_TITLE,
  BIOMARKER_PRIMARY_KEYWORD,
  SITE_URL,
} from '@/data/biomarkerPlatformSeo';
import styles from './BiomarkerNutritionPage.module.css';

const createMeta = (attributes: Record<string, string>) => {
  const element = document.createElement('meta');
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
  element.setAttribute('data-biomarker-seo', 'true');
  document.head.appendChild(element);
};

const createLink = (attributes: Record<string, string>) => {
  const element = document.createElement('link');
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
  element.setAttribute('data-biomarker-seo', 'true');
  document.head.appendChild(element);
};

const createJsonLd = (value: Record<string, unknown>) => {
  const element = document.createElement('script');
  element.type = 'application/ld+json';
  element.textContent = JSON.stringify(value);
  element.setAttribute('data-biomarker-seo', 'true');
  document.head.appendChild(element);
};

const useBiomarkerPageSeo = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.querySelectorAll('[data-biomarker-seo="true"]').forEach(element => element.remove());

    document.title = BIOMARKER_PAGE_TITLE;
    createMeta({ name: 'description', content: BIOMARKER_META_DESCRIPTION });
    createMeta({ name: 'keywords', content: BIOMARKER_PRIMARY_KEYWORD });
    createMeta({ property: 'og:title', content: BIOMARKER_PAGE_TITLE });
    createMeta({ property: 'og:description', content: BIOMARKER_META_DESCRIPTION });
    createMeta({ property: 'og:url', content: BIOMARKER_PAGE_CANONICAL_URL });
    createMeta({ property: 'og:type', content: 'website' });
    createMeta({ property: 'og:site_name', content: 'CalorieScience' });
    createMeta({ name: 'twitter:card', content: 'summary_large_image' });
    createMeta({ name: 'twitter:title', content: BIOMARKER_PAGE_TITLE });
    createMeta({ name: 'twitter:description', content: BIOMARKER_META_DESCRIPTION });
    createLink({ rel: 'canonical', href: BIOMARKER_PAGE_CANONICAL_URL });
    createLink({ rel: 'alternate', hreflang: 'en-GB', href: BIOMARKER_PAGE_CANONICAL_URL });

    createJsonLd({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'CalorieScience Biomarker-Informed Nutrition Platform',
      applicationCategory: 'HealthApplication',
      operatingSystem: 'Web',
      description: BIOMARKER_META_DESCRIPTION,
      url: BIOMARKER_PAGE_CANONICAL_URL,
      provider: {
        '@type': 'Organization',
        name: 'CalorieScience',
        url: SITE_URL,
      },
    });

    createJsonLd({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: BIOMARKER_FAQS.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    });

    createJsonLd({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Biomarker-Informed Nutrition', item: BIOMARKER_PAGE_CANONICAL_URL },
      ],
    });

    return () => {
      document.querySelectorAll('[data-biomarker-seo="true"]').forEach(element => element.remove());
      document.title = previousTitle;
    };
  }, []);
};

const HeroVisual: React.FC = () => {
  return (
    <div className={styles.heroVisual}>
      <img src={biomarkersScreen} alt="CalorieScience biomarkers dashboard showing extracted lab values ready for practitioner review" className={styles.heroVisualImage} loading="lazy" />
      <div className={styles.heroVisualBadge} aria-hidden="true">
        <img src={brandMarkLogo} alt="" />
      </div>
    </div>
  );
};

const WORKFLOW_STEPS = [
  {
    icon: UploadCloud,
    title: '1. Upload',
    text: 'Upload the client blood report to the client record.',
  },
  {
    icon: FlaskConical,
    title: '2. Extract',
    text: 'CalorieScience extracts supported biomarker values into a structured format for practitioner review.',
  },
  {
    icon: Link2,
    title: '3. Connect',
    text: 'Use the biomarkers alongside dietary intake, symptoms, health goals, medications, preferences and cultural food patterns.',
  },
  {
    icon: ClipboardCheck,
    title: '4. Generate and review',
    text: 'Create an AI-assisted meal plan and nutrition report, then edit, personalise and approve them before sharing.',
  },
];

const CAPABILITY_CARDS = [
  {
    title: 'Upload and extract blood report data',
    text: 'Bring a client blood report into the record and extract supported biomarker values into a structured view.',
  },
  {
    title: 'Use biomarkers in AI meal planning',
    text: 'Use supported biomarker information together with the wider client profile when generating an AI-assisted meal plan.',
  },
  {
    title: 'Connect the full client context',
    text: 'Consider dietary intake, symptoms, conditions, medications, goals, preferences and allergies together.',
  },
  {
    title: 'Review and edit every plan',
    text: 'Apply practitioner judgement, adjust meals and approve the final plan before it reaches the client.',
  },
  {
    title: 'Personalise food choices culturally',
    text: 'Adapt meals and recipes around familiar foods, dietary patterns and real-life preferences.',
  },
  {
    title: 'Create a nutrition report',
    text: 'Generate a practitioner-reviewed nutrition report within the same connected client workflow.',
  },
];

const AUDIENCE_ITEMS = [
  'Registered Dietitians',
  'Registered Nutritionists',
  'Nutritional Therapists',
  'Independent nutrition practices',
  'Small and multi-practitioner clinics',
];

const WHY_ITEMS = [
  {
    icon: Network,
    title: 'One connected client workflow',
    text: 'Bring biomarker information, dietary assessment and meal planning together.',
  },
  {
    icon: UserCheck,
    title: 'Practitioner control',
    text: 'Review and edit every plan before it reaches the client.',
  },
  {
    icon: UtensilsCrossed,
    title: 'Cultural personalisation',
    text: 'Build around the foods the client recognises and can realistically follow.',
  },
  {
    icon: MessageSquareText,
    title: 'Clearer client conversations',
    text: 'Explain how nutrition priorities connect with the available client information.',
  },
];

const FaqAccordion: React.FC = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className={styles.faqList}>
      {BIOMARKER_FAQS.map((faq, index) => {
        const isOpen = openIndex === index;
        return (
          <article className={styles.faqItem} key={faq.question}>
            <button
              type="button"
              className={styles.faqQuestion}
              onClick={() => setOpenIndex(isOpen ? -1 : index)}
              aria-expanded={isOpen}
            >
              <span>{faq.question}</span>
              <span className={styles.faqIcon} aria-hidden="true">
                {isOpen ? <ChevronUp /> : <ChevronDown />}
              </span>
            </button>
            {isOpen ? <p>{faq.answer}</p> : null}
          </article>
        );
      })}
    </div>
  );
};

const BiomarkerNutritionPage: React.FC = () => {
  useBiomarkerPageSeo();

  return (
    <div className={styles.page}>
      <div className={styles.heroBackdrop}>
        <Navbar />
        <section className={styles.hero}>
          <div className={styles.inner}>
            <p className={styles.breadcrumb}>Home&nbsp;/&nbsp;Platform&nbsp;/&nbsp;Biomarker-Informed Nutrition</p>
            <div className={styles.heroInner}>
              <div>
                <span className={styles.eyebrow}>
                  <FlaskConical size={13} className={styles.eyebrowIcon} aria-hidden="true" />
                  From lab results to practical food decisions
                </span>
                <h1 className={styles.heroTitle}>
                  Turn biomarker data into clearer, <em>practitioner-led</em> nutrition plans
                </h1>
                <p className={styles.heroSubtitle}>
                  CalorieScience helps nutrition professionals upload blood reports, extract nutrition-relevant
                  biomarkers and use them alongside dietary intake, symptoms, goals and food preferences to create an
                  editable, culturally personalised AI meal plan and nutrition report.
                </p>
                <div className={styles.heroCtas}>
                  <a href="/signup" className={styles.primaryButton} data-analytics-event="Landing CTA Clicked" data-analytics-id="biomarker_hero_explore" data-analytics-section="biomarker_platform">
                    Explore CalorieScience
                  </a>
                  <a href="/#ready-to-grow-practice" className={styles.secondaryButton} data-analytics-event="Landing CTA Clicked" data-analytics-id="biomarker_hero_demo" data-analytics-section="biomarker_platform">
                    Book a demo
                  </a>
                </div>
                <p className={styles.trustLine}>
                  Practitioner-reviewed &middot; Built to meet{' '}
                  <a href="/platform-privacy">UK and EU GDPR requirements</a> &middot; Not a diagnostic tool
                </p>
              </div>
              <HeroVisual />
            </div>
          </div>
        </section>
      </div>

      <main className={styles.main}>
        {/* Problem */}
        <section className={styles.section} id="the-problem">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>
                A lab report shows the numbers. <em>It does not create the nutrition plan.</em>
              </h2>
              <p className={styles.sectionText}>
                In many practices, biomarker information remains separated from dietary assessment and meal
                planning. Practitioners move between PDFs, spreadsheets, notes and planning tools before they can
                turn the findings into something the client can follow.
              </p>
            </div>
            <ul className={styles.problemList}>
              <li>Laboratory values must be reviewed and organised manually.</li>
              <li>The result may be disconnected from food intake, symptoms and the wider client history.</li>
              <li>The practitioner still has to translate the information into realistic food priorities.</li>
              <li>The reasoning behind the plan can be difficult to explain clearly to the client.</li>
            </ul>
            <p className={styles.problemCallout}>
              <b>CalorieScience brings these steps into one practitioner-led workflow.</b>
            </p>
          </div>
        </section>

        {/* Workflow */}
        <section className={`${styles.section} ${styles.sectionAlt}`} id="how-it-works">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <span className={styles.eyebrowPill}>From report to practitioner-reviewed plan</span>
              <h2 className={styles.sectionTitle}>
                One workflow, from <em>upload to approved plan</em>
              </h2>
            </div>
            <div className={styles.workflowGrid}>
              {WORKFLOW_STEPS.map(({ icon: Icon, title, text }) => (
                <div className={styles.workflowCard} key={title}>
                  <span className={styles.workflowIcon} aria-hidden="true">
                    <Icon size={20} />
                  </span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Capabilities */}
        <section className={styles.section} id="what-you-can-do">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>
                What practitioners can do in <em>CalorieScience</em>
              </h2>
            </div>
            <div className={styles.capabilityGrid}>
              {CAPABILITY_CARDS.map(({ title, text }) => (
                <div className={styles.capabilityCard} key={title}>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Practitioner in control */}
        <section className={`${styles.section} ${styles.sectionAlt}`} id="practitioner-in-control">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>
                Built to support the practitioner &mdash; <em>not replace them</em>
              </h2>
              <p className={styles.sectionText}>
                CalorieScience does not send an unsupervised AI recommendation directly to the client. The
                practitioner reviews the information, decides what is relevant, edits the plan and remains
                responsible for the final recommendation.
              </p>
            </div>
            <div className={styles.principleBox}>
              <p className={styles.principleLabel}>Core product principle</p>
              <p className={styles.principleText}>
                The platform reduces manual work. The practitioner remains responsible for the decision.
              </p>
            </div>
          </div>
        </section>

        {/* Designed for */}
        <section className={styles.section} id="designed-for">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>
                Designed for independent nutrition professionals <em>and growing clinics</em>
              </h2>
              <p className={styles.sectionText}>
                CalorieScience is designed for practitioners who want to bring biomarker information into
                personalised nutrition care without relying on separate spreadsheets, generic plan templates or
                disconnected practice tools.
              </p>
            </div>
            <ul className={styles.audienceChips}>
              {AUDIENCE_ITEMS.map(item => (
                <li key={item}>
                  <CheckCircle2 size={16} aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Why CalorieScience */}
        <section className={`${styles.section} ${styles.sectionAlt}`} id="why-caloriescience">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>Why CalorieScience</h2>
            </div>
            <div className={styles.privacyGrid}>
              {WHY_ITEMS.map(({ icon: Icon, title, text }) => (
                <div className={styles.privacyCard} key={title}>
                  <span className={styles.whyIcon} aria-hidden="true">
                    <Icon size={22} />
                  </span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className={styles.section} id="faq">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <span className={styles.eyebrow}>FAQ</span>
              <h2 className={styles.sectionTitle}>Frequently asked questions</h2>
            </div>
            <FaqAccordion />
          </div>
        </section>

        {/* Final CTA */}
        <section className={`${styles.section} ${styles.sectionAlt} ${styles.ctaSection}`} id="explore">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>
                See the <em>biomarker workflow</em> in CalorieScience
              </h2>
              <p className={styles.sectionText}>
                Bring lab information, dietary assessment and personalised planning into one practitioner-led workflow.
              </p>
            </div>
            <div className={styles.heroCtas} style={{ justifyContent: 'center' }}>
              <a href="/signup" className={styles.primaryButton} data-analytics-event="Landing CTA Clicked" data-analytics-id="biomarker_final_explore" data-analytics-section="biomarker_platform">
                Explore the platform
              </a>
              <a href="/#ready-to-grow-practice" className={styles.secondaryButton} data-analytics-event="Landing CTA Clicked" data-analytics-id="biomarker_final_demo" data-analytics-section="biomarker_platform">
                Book a demo
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BiomarkerNutritionPage;
